import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BLUE, BLUE_BG, BRAND, BRAND_LIGHT, ORANGE, ORANGE_BG } from "@/shared/constants.js";
import { STEP_PATHS, TOTAL_STEPS } from "@/modules/Quote/constants.js";
import { BINDER_NEEDED_STATUSES } from "@/modules/Payment/constants.js";
import { daysUntilDate, formatSubmissionListDate } from "@/shared/utils/format.js";
import { canDownloadPolicyDocuments, mapSubmissionToPolicyCard, parsePositiveSubmissionId } from "@/modules/Quote/utils/submission.js";
import { downloadCOI, downloadBinder } from "@/modules/Payment/utils/downloads.js";
import { loadDashboardSubmissions } from "@/modules/Dashboard/services/dashboardService.js";
import { Spinner } from "@/shared/components/Icon.jsx";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import { btnOutline, btnPrimary, dis } from "@/shared/utils/styles.js";
import { useStore } from "@/shared/store/useStore.js";
import dashboardStore from "@/modules/Dashboard/store/dashboardStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";
import bindStore from "@/modules/Payment/store/bindStore.js";
import { signOut, resetQuoteFlow } from "@/modules/Auth/services/authSessionService.js";
import { refreshPaymentOrder } from "@/modules/Payment/services/paymentOrderService.js";
import modalStore from "@/shared/store/modalStore.js";
import { MedMalGuardHeader } from "@/modules/Quote/components/MedMalGuardLanding.jsx";

const setShowAbout   = (v) => { modalStore.showAbout = v; };
const setShowPrivacy = (v) => { modalStore.showPrivacy = v; };
const setShowTerms   = (v) => { modalStore.showTerms = v; };

// Where an abandoned (non-payment) order resumes: /license — the first step
// after the submission is created.
const abandonedStep = 3;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [resumingSid, setResumingSid] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const doDownload = async (key, fn) => {
    setDownloadingKey(key);
    try { await fn(); } finally { setDownloadingKey(null); }
  };
  const dashView          = useStore(sessionStore, (s) => s.dashView);
  const sessionReady      = useStore(sessionStore, (s) => s.sessionReady);
  const bound             = useStore(sessionStore, (s) => s.bound);
  const insuredProfile    = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated   = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);
  const dashboardSubmissions        = useStore(dashboardStore, (s) => s.dashboardSubmissions);
  const dashboardSubmissionsLoading = useStore(dashboardStore, (s) => s.dashboardSubmissionsLoading);
  const dashboardSubmissionsError   = useStore(dashboardStore, (s) => s.dashboardSubmissionsError);

  const setDashView = (v) => { sessionStore.dashView = v; };
  const setDashboardSubmissions = (v) => { dashboardStore.dashboardSubmissions = v; };
  const setDashboardSubmissionsLoading = (v) => { dashboardStore.dashboardSubmissionsLoading = v; };
  const setDashboardSubmissionsError = (v) => { dashboardStore.dashboardSubmissionsError = v; };
  const setFlowSubmissionId = (v) => { submissionStore.flowSubmissionId = v; };
  const setPolicyDetailRow = (v) => { dashboardStore.policyDetailRow = v; };
  const setStep = (v) => { submissionStore.step = v; };
  const resetCompletionAndPaymentOrderBootstrap = () => { bindStore.clear(); paymentOrderStore.clear(); };
  const goTo = (n) => { submissionStore.step = n; navigate(STEP_PATHS[n]); };
  const goToFromDashboard = (n) => { sessionStore.dashView = null; submissionStore.step = n; navigate(STEP_PATHS[n]); };
  const handleSignOut = async () => { await signOut(); navigate("/"); };

  // Resume an open order: fetch /insured/order first so paymentOrderStore
  // is populated by the time we navigate into the flow, then route based
  // on the order's current status.
  const handleResumeOrder = async (sid, status) => {
    if (!sid || resumingSid) return;
    setResumeError(null);
    setResumingSid(sid);
    try {
      submissionStore.flowSubmissionId = sid;
      resetCompletionAndPaymentOrderBootstrap();
      const order = await refreshPaymentOrder(sid);
      if (!order) {
        setResumeError("Could not load order details. Please try again.");
        return;
      }
      sessionStore.dashView = null;
      if (status === "pay") { goToFromDashboard(7); return; } // /payment
      if (status === "pending review") { navigate("/underwriter-review"); return; }
      if (BINDER_NEEDED_STATUSES.has(status)) { navigate("/binder-invoice"); return; }
      goToFromDashboard(abandonedStep);
    } catch (err) {
      setResumeError(err?.message || "Could not load order details.");
    } finally {
      setResumingSid(null);
    }
  };

  // All hooks must run on every render — keep this above any conditional
  // returns so React's hook ordering stays stable across renders.
  //
  // Auto-set dashView when the user lands on /dashboard without one set
  // (e.g. FlowLayout's guard redirected an authenticated user with missing
  // quote state). The non-authenticated case falls through to the
  // Navigate("/") below so they don't get stuck on a blank page.
  useEffect(() => {
    if (dashView === null && isAuthenticated) sessionStore.dashView = "dashboard";
  }, [dashView, isAuthenticated]);

  // Fetch the submissions list once per mount on /dashboard. Writes to
  // the store directly (no inline setter deps → no re-fire loop).
  useEffect(() => {
    let cancelled = false;
    dashboardStore.dashboardSubmissionsLoading = true;
    dashboardStore.dashboardSubmissionsError = null;
    (async () => {
      try {
        const rows = await loadDashboardSubmissions();
        if (cancelled) return;
        dashboardStore.dashboardSubmissions = rows;
      } catch (err) {
        if (cancelled) return;
        dashboardStore.dashboardSubmissionsError = err.message;
        dashboardStore.dashboardSubmissions = [];
      } finally {
        if (!cancelled) dashboardStore.dashboardSubmissionsLoading = false;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!sessionReady && !bound) {
    return (
      <div className="dash-page">
        <div className="app-header">
          <MedMalGuardHeader />
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <Loader label="Loading…" />
        </div>
      </div>
    );
  }

  if (dashView === null) {
    if (isAuthenticated) return null; // effect above is about to set dashView
    return <Navigate to="/signin" replace />;
  }

  const welcomeFirstName = (() => {
    const u = insuredProfile?.name || insuredProfile?.username;
    if (!u) return "there";
    if (String(u).includes("@")) return String(u).split("@")[0];
    return String(u).split(/\s+/)[0] || "there";
  })();

  const dashboardSubmissionStatus = (s) =>
    (s?.statusname || s?.policystatus || s?.filterstatus || s?.workFlowStatus || s?.workflowstatus || "")
      .toString()
      .trim()
      .toLowerCase();
  const isResumeDashboardSubmission = (s) => {
    if (!s) return false;
    const status = dashboardSubmissionStatus(s);
    if (status === "pay" || status === "order" || status === "pending review" || BINDER_NEEDED_STATUSES.has(status)) return true;
    if (s?.isopenorder === true) return !s.ispolicyactive;
    return false;
  };
  const hasAbandonedQuote = dashboardSubmissions.some(isResumeDashboardSubmission);

  const renewalPick = dashboardSubmissions
    .filter((s) => s.ispolicyactive && s.expireddate)
    .map((s) => ({ s, daysUntil: daysUntilDate(s.expireddate) }))
    .filter((x) => x.daysUntil != null && x.daysUntil <= 0)
    .map((x) => ({ s: x.s, daysSince: Math.abs(x.daysUntil) }))
    .sort((a, b) => a.daysSince - b.daysSince)[0];

  const hasPoliciesToShow = dashboardSubmissions.length > 0;

  return (
    // Mobile shows the shared MedMalGuard header (logo + hamburger) + card.
    // Desktop (>=1024px, via .dash-page CSS in responsive.css) escapes the
    // card, shows the MedMalGuard header (same as the pricing/sign-in pages),
    // and lays the content out in a centered column on the app canvas.
    <div className="dash-page">
      {/* MedMalGuard header at every viewport — collapses to logo +
          hamburger on phones, same as the landing page. */}
      <div className="app-header">
        <MedMalGuardHeader />
      </div>

      <div className="dash-body" style={{ flex: 1, padding: "0 18px 18px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
       <div className="dash-card">


        {dashView === "dashboard" && <>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, color: "#1a1a1a", margin: "12px 0 4px" }}>Welcome back, {welcomeFirstName}</h2>
          <p style={{ fontSize: 13, color: "#777", marginBottom: 16 }}>Here's your account overview.</p>

          {hasAbandonedQuote && (
            <div style={{ background: ORANGE_BG, borderRadius: 12, padding: "14px 16px", marginBottom: 12, border: `1px solid ${ORANGE}22` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                </svg>
                <span style={{ fontSize: 13, fontWeight: 500, color: ORANGE }}>Incomplete application</span>
              </div>
              {(() => {
                const openOrders = dashboardSubmissions.filter(isResumeDashboardSubmission);
                const count = openOrders.length;
                if (count === 0) return null;
                return (
                  <>
                    <div style={{ fontSize: 12, color: "#8B4513", lineHeight: 1.6, marginBottom: 10 }}>
                      You have {count} incomplete application{count === 1 ? "" : "s"}. Select one to resume.
                    </div>

                    <div style={{ background: "#fff", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                      <div style={{ display: "flex", gap: 3, marginBottom: 6 }}>
                        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < abandonedStep ? BRAND : "#e0e0de" }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: "#999" }}>Resume will continue at your saved step or payment.</div>
                    </div>

                    {openOrders.map((s) => {
                      const sid = parsePositiveSubmissionId(s?.submissionid);
                      const label = s?.quotenumber || s?.policynumber || (sid != null ? `#${sid}` : "—");
                      const status = (s?.statusname || s?.policystatus || s?.filterstatus || s?.workFlowStatus || s?.workflowstatus || "").toString().trim().toLowerCase();
                      const isLoading = resumingSid === sid;
                      const isDisabled = resumingSid != null && !isLoading;
                      return (
                        <button
                          key={sid ?? label}
                          type="button"
                          disabled={isDisabled || isLoading}
                          onClick={() => handleResumeOrder(sid, status)}
                          style={{
                            ...btnPrimary,
                            background: ORANGE,
                            fontSize: 13,
                            padding: "11px 0",
                            boxShadow: "0 2px 8px rgba(216,90,48,0.2)",
                            marginTop: 8,
                            opacity: isDisabled ? 0.55 : 1,
                            cursor: isDisabled || isLoading ? "default" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          {isLoading ? <><Spinner />Loading order…</> : `Resume ${label}`}
                        </button>
                      );
                    })}
                    {resumeError && (
                      <div style={{ marginTop: 8 }}>
                        <Alert type="error" message={resumeError} />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {renewalPick && (
            <div style={{ background: BLUE_BG, borderRadius: 12, padding: "14px 16px", marginBottom: 12, border: `1px solid ${BLUE}22` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                </svg>
                <span style={{ fontSize: 13, fontWeight: 500, color: BLUE }}>Renewal reminder</span>
              </div>
              <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6, marginBottom: 10 }}>
                Your policy <strong style={{ color: "#333" }}>{renewalPick.s.policynumber || renewalPick.s.quotenumber || `#${renewalPick.s.submissionid}`}</strong> expired on <strong style={{ color: "#333" }}>{formatSubmissionListDate(renewalPick.s.expireddate)}</strong> — {renewalPick.daysSince === 0 ? "today" : renewalPick.daysSince === 1 ? "1 day ago" : `${renewalPick.daysSince} days ago`}. Renew now to avoid a gap in coverage.
              </div>
              <button type="button" onClick={() => { resetQuoteFlow(); setDashView(null); goTo(0); }} style={{ ...btnPrimary, background: BLUE, fontSize: 13, padding: "11px 0", boxShadow: "0 2px 8px rgba(24,95,165,0.2)" }}>
                Renew now
              </button>
            </div>
          )}

          {dashboardSubmissionsLoading && <Loader label="Loading policies…" />}
          <Alert type="error" message={dashboardSubmissionsError} />
          {!dashboardSubmissionsLoading && !dashboardSubmissionsError && !hasPoliciesToShow && (
            <Alert type="info" message="No policies or submissions on file yet." />
          )}
          {!dashboardSubmissionsLoading && !dashboardSubmissionsError && hasPoliciesToShow && (
            <>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8, marginTop: 4 }}>My policies</div>
              {dashboardSubmissions.map((s) => {
                const pol = mapSubmissionToPolicyCard(s);
                const canDownload = canDownloadPolicyDocuments(s);
                return (
                  <div key={s.submissionid ?? pol.num} style={{ background: "#f7f7f5", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: "#444" }}>{pol.num}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 500, padding: "3px 8px", borderRadius: 10,
                        color: pol.isActive ? BRAND : "#888",
                        background: pol.isActive ? BRAND_LIGHT : "#eee",
                      }}>{pol.statusLabel}</span>
                    </div>
                    {[
                      ["Specialty", pol.lim],
                      ["Policy period", `${pol.eff} – ${pol.exp}`],
                      ["Retroactive date", pol.retro],
                      ["Balance", pol.prem],
                      ["Practice ZIP", pol.zipState],
                    ].map(([l, v]) => (
                      <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                        <span style={{ fontSize: 11.5, color: "#888" }}>{l}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 500, color: "#444" }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" onClick={() => { setPolicyDetailRow(s); navigate(`/order-details?submissionId=${encodeURIComponent(String(s.submissionid))}`); }} style={{ ...btnOutline, flex: 1, fontSize: 11, padding: "8px 0", marginTop: 0 }}>View details</button>
                        <button
                          type="button"
                          style={dis({ ...btnOutline, flex: 1, fontSize: 11, padding: "8px 0", marginTop: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }, canDownload && downloadingKey !== `coi-${s.submissionid}`)}
                          onClick={() => { if (canDownload && !downloadingKey) doDownload(`coi-${s.submissionid}`, () => downloadCOI(s.submissionid, setDashboardSubmissionsError)); }}
                        >
                          {downloadingKey === `coi-${s.submissionid}` ? <><Spinner />Downloading…</> : "Download COI"}
                        </button>
                        <button
                          type="button"
                          style={dis({ ...btnOutline, flex: 1, fontSize: 11, padding: "8px 0", marginTop: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }, canDownload && downloadingKey !== `binder-${s.submissionid}`)}
                          onClick={() => { if (canDownload && !downloadingKey) doDownload(`binder-${s.submissionid}`, () => downloadBinder(s.submissionid, canDownload, setDashboardSubmissionsError)); }}
                        >
                          {downloadingKey === `binder-${s.submissionid}` ? <><Spinner />Downloading…</> : "Binder"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8, marginTop: 4 }}>Quick actions</div>
          {[
            { label: "Start a new quote", sub: "Get coverage for a new policy period", action: () => { resetQuoteFlow(); goToFromDashboard(0); } },
            { label: "View my profile", sub: "License, scope, and practice details", action: () => navigate("/profile") },
          ].map((item) => (
            <div key={item.label} onClick={item.action} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 14px", background: "#f7f7f5", borderRadius: 10, marginBottom: 8,
              cursor: item.action ? "pointer" : "default", transition: "background 0.15s",
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>{item.label}</div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{item.sub}</div>
              </div>
              {item.action && <svg viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}><path d="M9 18l6-6-6-6" /></svg>}
            </div>
          ))}

          <Spacer />
          <button type="button" onClick={handleSignOut} style={{ ...btnOutline, fontSize: 12, marginTop: 0 }}>Sign out</button>
        </>}

       </div>
      </div>

      <div className="dash-footer" style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", textAlign: "center", fontSize: 10, color: "#ccc" }}>
        SelectFirst Insurance Services · (888) 966-3881 · <span onClick={() => setShowAbout(true)} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>About</span> · <span onClick={() => setShowPrivacy(true)} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Privacy</span> · <span onClick={() => setShowTerms(true)} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Terms</span>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
